import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  colors,
  ConnectedPlusIcon,
  EllipsisIcon,
  fontFamilies,
  IconButton,
} from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import {
  ClientColumn,
  SupplierColumn,
  PurchaseOrderNumberColumn,
  ProjectNumberColumn,
} from "../common";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import { useReportContext } from "../../report_context";
import { oauth } from "../../../../utils";
import { Bill } from "../../../../types/bills";
import { getDefaultBill, saveBill } from "../../../../redux/bills";
import BillPopup from "../../../bills/BillPopup";
import {
  getPromostandardsInvoices,
  confirmPromostandardsInvoice,
} from "../../../../redux/promostandardsInvoices";
import { useDispatch, useSelector } from "react-redux";
import { PromostandardsInvoice } from "../../../../types/promostandards_invoice";
import { useIdentity, useUserFlags } from "../../../../hooks";
import { useProjectBillSave } from "../../../bills/useProjectBillSave";
import { trackEvent } from "../../../../utils/analytics";

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> =
{
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_name", "job_number"],
  accessorKey: "job_name",
  canSort: true,
  orderByField: "job_name",
  width: 150,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ job_name, job_number }) => ({
    href: `/project/${job_number}`,
    text: job_name,
  }),
};

export const DateShippedColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Date&nbsp;Shipped</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_shipped"],
  accessorKey: "date_shipped",
  canSort: true,
  orderByField: "date_shipped",
  width: 120,
};

export const UnbilledAmountColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: <>Unbilled&nbsp;Amount</>,
  type: BASE_COLUMN_TYPES.Money,
  fields: ["unbilled_amount", "currency_id"],
  accessorKey: "unbilled_amount",
  canSort: true,
  orderByField: "unbilled_amount",
  width: 95,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ unbilled_amount, currency_id }) => ({
    amount: unbilled_amount,
    currency_id,
  }),
};

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }
  &.dropdown-menu-open > .dropdown-menu-trigger {
    border: 2px solid ${colors.primary1.main};
  }
  &&& > ul {
    top: -0.5rem;
    right: 3rem;
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    min-width: 200px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

function PurchaseOrderActions({
  purchase_order_id,
  epo_status,
}: {
  purchase_order_id: string;
  epo_status: string;
}) {
  const [bill, setBill] = useState<Bill | undefined>();
  const { fetchReport } = useReportContext();
  const dispatch = useDispatch();
  const handleProjectBillSave = useProjectBillSave();

  const identity = useIdentity();
  const psInvoiceData = useSelector((state: any) =>
    state.entities.promostandards_invoices?.data?.find(
      (invoice: PromostandardsInvoice) =>
        invoice.purchase_order_id === purchase_order_id
    )
  );

  useEffect(() => {
    if (epo_status === "SUCCESS") {
      dispatch(getPromostandardsInvoices(purchase_order_id));
    }
  }, [epo_status, dispatch, purchase_order_id]);

  const onConfirmBill = useCallback(async () => {
    try {
      const tempBill = await getDefaultBill([purchase_order_id]);
      await dispatch(saveBill(tempBill));
      await dispatch(confirmPromostandardsInvoice(purchase_order_id));
      toast.success("Bill confirmed");
      fetchReport();
    } catch (error) {
      toast.error("Unable to confirm bill");
    }
  }, [purchase_order_id, dispatch, fetchReport]);

  const options = useMemo(() => {
    const matchStatus = psInvoiceData?.match_status;

    if (matchStatus === "PENDING") {
      return [
        {
          value: (
            <IconButton
              variant="text"
              size="small"
              iconPosition="left"
              Icon={ConnectedPlusIcon({ color: colors.neutrals["60"] })}
              style={{
                width: "calc(100% - 13px)",
                margin: "4px 6.5px",
                color: colors.neutrals["60"],
              }}
            >
              Expecting Bill
            </IconButton>
          ),
          hideOnClick: true,
          onClick: () => { },
        },
      ];
    }

    if (matchStatus === "MATCHED") {
      return [
        {
          value: (
            <IconButton
              variant="custom"
              size="small"
              iconPosition="left"
              variantBorderColor="transparent"
              variantBg={colors.secondary3.main}
              variantColor={colors.neutrals.white}
              variantHoverBorderColor={colors.secondary3["70"]}
              variantHoverBg={colors.secondary3["70"]}
              style={{ width: "calc(100% - 13px)", margin: "4px 6.5px" }}
              Icon={ConnectedPlusIcon}
            >
              Confirm Bill
            </IconButton>
          ),
          hideOnClick: true,
          onClick: async () => {
            await onConfirmBill();
          },
        },
        {
          value: (
            <IconButton
              variant="text"
              size="small"
              iconPosition="left"
              Icon={ConnectedPlusIcon}
              style={{ width: "calc(100% - 13px)", margin: "4px 6.5px" }}
            >
              Review Bill
            </IconButton>
          ),
          hideOnClick: true,
          onClick: async () => {
            const bill = await getDefaultBill([purchase_order_id]);
            trackEvent("Bill - Review", {
              tenant_id: identity.tenant_id,
              login_email: identity.login_email,
            });
            setBill(bill);
          },
        },
      ];
    }

    return [
      {
        value: "Create Bill",
        hideOnClick: true,
        onClick: async () => {
          const bill = await getDefaultBill([purchase_order_id]);
          setBill(bill);
        },
      },
      {
        value: "Mark as Billed",
        hideOnClick: true,
        onClick: async () => {
          try {
            await oauth("PUT", `purchaseorder/${purchase_order_id}`, {
              billed: true,
            });
            toast.success("PO marked as billed");
            fetchReport();
          } catch (e) {
            toast.error("Unable to mark PO as billed");
          }
        },
      },
    ];
  }, [
    purchase_order_id,
    psInvoiceData,
    fetchReport,
    identity.tenant_id,
    identity.login_email,
    onConfirmBill,
  ]);
  const onClose = useCallback(() => setBill(undefined), []);

  return (
    <>
      {bill && (
        <BillPopup
          bill={bill}
          isReview={psInvoiceData?.match_status === "MATCHED"}
          onClose={onClose}
          onSave={handleProjectBillSave}
        />
      )}
      <StyledDropdownMenu options={options} align="right">
        <MenuTrigger>
          <EllipsisIcon altText="Actions" />
        </MenuTrigger>
      </StyledDropdownMenu>
    </>
  );
}

const ActionColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> = {
  header: "Actions",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["purchase_order_id", "epo_status"],
  accessorKey: "purchase_order_id",
  canSort: false,
  width: 120,
  justify: "center",
  dataTransform: ({ purchase_order_id, epo_status }) => ({
    cell: (
      <PurchaseOrderActions
        purchase_order_id={purchase_order_id}
        epo_status={epo_status}
      />
    ),
  }),
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  PurchaseOrderNumberColumn,
  SupplierColumn,
  ClientColumn,
  UnbilledAmountColumn,
  DateShippedColumn,
  ProjectNumberColumn,
  ProjectNameColumn,
  ActionColumn,
];
